import { EChart } from "@/lib/EChart";
import type { Infringement } from "@/types/domain";
import { useThemeColors } from "@/hooks/useThemeColors";
import { useFilteredInfringements } from "@/hooks/useFilteredInfringements";
import { useDashboardFilter } from "@/contexts/DashboardFilterContext";
import { WidgetShell } from "@/components/dashboard/WidgetShell";
import { TYPE_CONFIG } from "@/widgets/infringements-table/badges";

interface InfringementTypesTreemapProps {
  infringements: Infringement[];
  onTypeClick?: (type: string) => void;
  activeType?: string;
}

const TYPE_LABELS: Record<string, string> = {
  restreaming: "Restreaming",
  cdn_leeching: "Leeching",
  theft_for_restreaming: "Theft for Restreaming",
};

function InfringementTypesTreemap({
  infringements,
  onTypeClick,
  activeType,
}: InfringementTypesTreemapProps) {
  const colors = useThemeColors();

  const typeCounts = new Map<string, number>();
  infringements.forEach((inf) => {
    const type = inf.infringement_type;
    typeCounts.set(type, (typeCounts.get(type) || 0) + 1);
  });

  const data = Array.from(typeCounts.entries()).map(([type, count]) => ({
    name: TYPE_LABELS[type] || type,
    value: count,
    itemStyle: {
      color: TYPE_CONFIG[type as keyof typeof TYPE_CONFIG]?.hex ?? colors.textMuted,
      opacity: activeType && activeType !== type ? 0.35 : 1,
    },
  }));

  const total = data.reduce((sum, item) => sum + item.value, 0);

  // Reverse label→key map for click handler
  const LABEL_TO_KEY: Record<string, string> = Object.fromEntries(
    Object.entries(TYPE_LABELS).map(([k, v]) => [v, k]),
  );

  const option = {
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
      backgroundColor: colors.surfaceElevated,
      borderColor: colors.border,
      borderWidth: 1,
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      formatter: (params: any) => {
        const percentage = ((params.value / total) * 100).toFixed(1);
        return `<div style="font-weight:600;margin-bottom:4px;text-transform:uppercase;font-size:11px;">${params.name}</div>
                <div style="display:flex;align-items:center;gap:8px;">
                  <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${params.color}"></span>
                  <span style="font-weight:600;font-size:14px;">${params.value}</span>
                  <span style="color:${colors.textMuted};font-size:11px;">(${percentage}%)</span>
                </div>`;
      },
    },
    legend: {
      orient: "vertical",
      right: "8%",
      top: "center",
      itemGap: 16,
      itemWidth: 12,
      itemHeight: 12,
      icon: "circle",
      formatter: (name: string) => {
        const item = data.find((d) => d.name === name);
        const percentage = item ? ((item.value / total) * 100).toFixed(0) : "0";
        return `{name|${name}}\n{value|${item?.value || 0}} {percent|(${percentage}%)}`;
      },
      textStyle: {
        color: colors.textSecondary,
        fontSize: 12,
        fontFamily: "Inter, sans-serif",
        rich: {
          name: { fontSize: 12, fontWeight: 600, color: colors.textSecondary, lineHeight: 20 },
          value: { fontSize: 18, fontWeight: 700, color: colors.textPrimary, lineHeight: 22 },
          percent: { fontSize: 11, color: colors.textMuted, lineHeight: 22 },
        },
      },
    },
    series: [
      {
        type: "pie",
        radius: ["55%", "85%"],
        center: ["30%", "50%"],
        avoidLabelOverlap: false,
        label: { show: false },
        labelLine: { show: false },
        emphasis: {
          scale: true,
          scaleSize: 8,
          itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: "rgba(0,0,0,0.5)" },
        },
        data,
      },
    ],
  };

  return (
    <div className="h-full">
      {total === 0 ? (
        <div className="flex items-center justify-center h-full text-sm text-muted">
          No data available
        </div>
      ) : (
        <EChart
          option={option}
          style={{ height: "100%" }}
          onEvents={
            onTypeClick
              ? {
                  click: (params: any) => {
                    const key = LABEL_TO_KEY[params.name] ?? params.name;
                    onTypeClick(key);
                  },
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

export function InfringementTypesWidget() {
  const { infringements } = useFilteredInfringements();
  const { filters, setFilter } = useDashboardFilter();

  return (
    <WidgetShell title="Infringement Types">
      <div className="flex-1 min-h-0 px-4 pb-2">
        <InfringementTypesTreemap
          infringements={infringements}
          onTypeClick={(type) => setFilter({ infringementType: type })}
          activeType={filters.infringementType}
        />
      </div>
    </WidgetShell>
  );
}
